import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { 
  AlertTriangle, CheckCircle, Clock, Trash2, RotateCcw, 
  ShieldAlert, UserX, Eye, MessageSquare, Filter, ShieldCheck,
  Search, Check, X
} from "lucide-react";

export default function IDGModerationTab() {
  const [subTab, setSubTab] = useState("reports");
  const [reports, setReports] = useState([]);
  const [queue, setQueue] = useState({ topics: [], posts: [] });
  const [trash, setTrash] = useState({ topics: [], posts: [] });
  const [loading, setLoading] = useState(false);
  const [reportFilter, setReportFilter] = useState("pending");

  const [warnModal, setWarnModal] = useState({ open: false, userId: "", username: "", points: 1, reason: "" });

  const loadReports = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/moderation/reports?status=${reportFilter}`);
      setReports(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadQueue = async () => {
    try {
      setLoading(true);
      const res = await api.get("/moderation/queue");
      setQueue(res.data || { topics: [], posts: [] });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadTrash = async () => {
    try {
      setLoading(true);
      const res = await api.get("/moderation/trash");
      setTrash(res.data || { topics: [], posts: [] });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (subTab === "reports") loadReports();
    if (subTab === "queue") loadQueue();
    if (subTab === "trash") loadTrash();
  }, [subTab, reportFilter]);

  const handleResolveReport = async (reportId, action) => {
    try {
      await api.post(`/moderation/reports/${reportId}/resolve`, { action, note: `Rezolvat (${action})` });
      toast.success(action === "resolved" ? "Raport marcat ca rezolvat!" : "Raport respins!");
      loadReports();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleApproveQueue = async (itemId) => {
    try {
      await api.post(`/moderation/queue/${itemId}/approve`);
      toast.success("Continut aprobat si publicat!");
      loadQueue();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleRejectQueue = async (itemId) => {
    try {
      await api.post(`/moderation/queue/${itemId}/reject`, { action: "reject", reason: "Continut neconform" });
      toast.success("Continut respins si eliminat!");
      loadQueue();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleRestoreTrash = async (itemId) => {
    try {
      await api.post(`/moderation/trash/${itemId}/restore`);
      toast.success("Continut restaurat cu succes!");
      loadTrash();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handlePermanentDelete = async (itemId) => {
    if (!window.confirm("Sigur doriti sa stergeti definitiv acest element din baza de date?")) return;
    try {
      await api.delete(`/moderation/trash/${itemId}/permanent`);
      toast.success("Element sters definitiv!");
      loadTrash();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleIssueWarn = async () => {
    if (!warnModal.userId || !warnModal.reason.trim()) {
      toast.error("Specificati motivul avertismentului!");
      return;
    }
    try {
      const res = await api.post("/moderation/warn", {
        user_id: warnModal.userId,
        points: parseInt(warnModal.points, 10) || 1,
        reason: warnModal.reason.trim()
      });
      toast.success(`Avertisment emis! Total puncte utilizator: ${res.data.total_points}`);
      setWarnModal({ open: false, userId: "", username: "", points: 1, reason: "" });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="idg-moderation-suite">
      <div className="flex items-center gap-2 border-b border-white/10 pb-3 flex-wrap">
        {[
          { id: "reports", label: "Raportari Continut & Membri", icon: AlertTriangle, count: reports.filter(r => r.status === "pending").length },
          { id: "queue", label: "Coada de Aprobare", icon: Clock, count: (queue.topics?.length || 0) + (queue.posts?.length || 0) },
          { id: "trash", label: "Cos de Gunoi (Soft Delete)", icon: Trash2, count: (trash.topics?.length || 0) + (trash.posts?.length || 0) }
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`px-4 py-2 text-xs font-chakra font-black uppercase tracking-wider flex items-center gap-2 transition-all ${
              subTab === t.id
                ? "bg-[var(--tf-primary)] text-black shadow-lg"
                : "bg-white/5 text-zinc-400 hover:text-white hover:bg-white/10 border border-white/10"
            }`}
          >
            <t.icon className="w-3.5 h-3.5" />
            <span>{t.label}</span>
            {t.count > 0 && (
              <span className={`px-1.5 py-0.2 text-[9px] font-mono font-bold rounded ${subTab === t.id ? "bg-black text-white" : "bg-red-500/20 text-red-400 border border-red-500/40"}`}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {subTab === "reports" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3 bg-[#0b0e15] border border-white/10 p-3.5 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-mono text-zinc-400 uppercase font-bold">Filtru Status:</span>
              {["pending", "resolved", "dismissed", "all"].map((st) => (
                <button
                  key={st}
                  onClick={() => setReportFilter(st)}
                  className={`px-2.5 py-1 text-[10px] font-mono font-bold uppercase transition-colors ${
                    reportFilter === st ? "bg-[var(--tf-primary)] text-black" : "bg-white/5 text-zinc-400 hover:text-white"
                  }`}
                >
                  {st === "pending" ? "In Asteptare" : st === "resolved" ? "Rezolvate" : st === "dismissed" ? "Respinse" : "Toate"}
                </button>
              ))}
            </div>
            <button onClick={loadReports} className="px-3 py-1 bg-white/5 hover:bg-white/10 text-xs font-mono text-zinc-300">
              Reimprospateaza
            </button>
          </div>

          {loading ? (
            <div className="p-8 text-center text-xs font-mono text-zinc-500">Se incarca raportarile...</div>
          ) : reports.length === 0 ? (
            <div className="p-12 text-center bg-[#0b0e15] border border-white/10 space-y-2">
              <ShieldCheck className="w-10 h-10 text-emerald-400 mx-auto opacity-50" />
              <p className="text-xs font-chakra font-bold text-white uppercase">Nu exista raportari in aceasta sectiune</p>
              <p className="text-[11px] font-mono text-zinc-500">Comunitatea este curata si conforma regulamentului.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reports.map((rep) => (
                <div key={rep.id} className="bg-[#0b0e15] border border-white/10 p-4 space-y-3">
                  <div className="flex items-center justify-between border-b border-white/5 pb-2.5">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 text-[9px] font-mono uppercase font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40">
                        {rep.target_type}
                      </span>
                      <span className="text-xs font-chakra font-bold text-white">
                        Raportat de: <span className="text-[var(--tf-primary)]">{rep.reporter_username}</span>
                      </span>
                    </div>
                    <span className="text-[10px] font-mono text-zinc-500">{new Date(rep.created_at).toLocaleString("ro-RO")}</span>
                  </div>

                  <div>
                    <p className="text-xs font-mono font-bold text-red-400">Motiv: {rep.reason}</p>
                    {rep.details && <p className="text-[11px] font-mono text-zinc-400 mt-1 bg-black/40 p-2 border border-white/5">{rep.details}</p>}
                  </div>

                  {rep.status === "pending" && (
                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
                      <button
                        onClick={() => setWarnModal({ open: true, userId: rep.target_id, username: rep.target_id, points: 1, reason: `Raport: ${rep.reason}` })}
                        className="px-3 py-1.5 bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30 text-[11px] font-chakra font-bold uppercase flex items-center gap-1.5"
                      >
                        <ShieldAlert className="w-3.5 h-3.5" />
                        <span>Avertizeaza Autor</span>
                      </button>
                      <button
                        onClick={() => handleResolveReport(rep.id, "dismissed")}
                        className="px-3 py-1.5 bg-white/5 text-zinc-400 hover:text-white border border-white/10 text-[11px] font-chakra font-bold uppercase flex items-center gap-1.5"
                      >
                        <X className="w-3.5 h-3.5" />
                        <span>Respinge</span>
                      </button>
                      <button
                        onClick={() => handleResolveReport(rep.id, "resolved")}
                        className="px-3 py-1.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30 text-[11px] font-chakra font-bold uppercase flex items-center gap-1.5"
                      >
                        <Check className="w-3.5 h-3.5" />
                        <span>Rezolvat</span>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {subTab === "queue" && (
        <div className="space-y-4">
          {loading ? (
            <div className="p-8 text-center text-xs font-mono text-zinc-500">Se incarca coada de moderare...</div>
          ) : (queue.topics?.length === 0 && queue.posts?.length === 0) ? (
            <div className="p-12 text-center bg-[#0b0e15] border border-white/10 space-y-2">
              <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto opacity-50" />
              <p className="text-xs font-chakra font-bold text-white uppercase">Coada de moderare goala</p>
              <p className="text-[11px] font-mono text-zinc-500">Toate postarile si topicurile au fost deja aprobate.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {(queue.topics || []).map((t) => (
                <div key={t.id} className="bg-[#0b0e15] border border-white/10 p-4 space-y-2 flex items-center justify-between">
                  <div>
                    <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase bg-amber-500/20 text-amber-300 border border-amber-500/40 mr-2 font-bold">Topic</span>
                    <span className="text-xs font-chakra font-bold text-white">{t.title}</span>
                    <p className="text-[10px] font-mono text-zinc-500 mt-0.5">Autor: {t.author_username} // {new Date(t.created_at).toLocaleString("ro-RO")}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleApproveQueue(t.id)} className="px-3 py-1.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[11px] font-chakra font-bold uppercase">Aproba</button>
                    <button onClick={() => handleRejectQueue(t.id)} className="px-3 py-1.5 bg-red-500/20 text-red-300 border border-red-500/40 text-[11px] font-chakra font-bold uppercase">Respinge</button>
                  </div>
                </div>
              ))}
              {(queue.posts || []).map((p) => (
                <div key={p.id} className="bg-[#0b0e15] border border-white/10 p-4 space-y-2 flex items-center justify-between">
                  <div>
                    <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase bg-blue-500/20 text-blue-300 border border-blue-500/40 mr-2 font-bold">Raspuns Postare</span>
                    <span className="text-xs font-mono text-zinc-300">{p.content?.substring(0, 80)}...</span>
                    <p className="text-[10px] font-mono text-zinc-500 mt-0.5">Autor: {p.author_username} // {new Date(p.created_at).toLocaleString("ro-RO")}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleApproveQueue(p.id)} className="px-3 py-1.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[11px] font-chakra font-bold uppercase">Aproba</button>
                    <button onClick={() => handleRejectQueue(p.id)} className="px-3 py-1.5 bg-red-500/20 text-red-300 border border-red-500/40 text-[11px] font-chakra font-bold uppercase">Respinge</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {subTab === "trash" && (
        <div className="space-y-4">
          {loading ? (
            <div className="p-8 text-center text-xs font-mono text-zinc-500">Se incarca cosul de gunoi...</div>
          ) : (trash.topics?.length === 0 && trash.posts?.length === 0) ? (
            <div className="p-12 text-center bg-[#0b0e15] border border-white/10 space-y-2">
              <Trash2 className="w-10 h-10 text-zinc-600 mx-auto opacity-50" />
              <p className="text-xs font-chakra font-bold text-white uppercase">Cosul de gunoi este gol</p>
              <p className="text-[11px] font-mono text-zinc-500">Nu exista elemente sterse temporar.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {(trash.topics || []).map((t) => (
                <div key={t.id} className="bg-[#0b0e15] border border-white/10 p-4 space-y-2 flex items-center justify-between">
                  <div>
                    <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase bg-red-500/20 text-red-300 border border-red-500/40 mr-2 font-bold">Topic Sters</span>
                    <span className="text-xs font-chakra font-bold text-white">{t.title}</span>
                    <p className="text-[10px] font-mono text-zinc-500 mt-0.5">Autor: {t.author_username}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleRestoreTrash(t.id)} className="px-3 py-1.5 bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 text-[11px] font-chakra font-bold uppercase flex items-center gap-1">
                      <RotateCcw className="w-3.5 h-3.5" />
                      <span>Restaureaza</span>
                    </button>
                    <button onClick={() => handlePermanentDelete(t.id)} className="px-3 py-1.5 bg-red-500/20 text-red-300 border border-red-500/40 text-[11px] font-chakra font-bold uppercase flex items-center gap-1">
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>Sterge Definitiv</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {warnModal.open && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#0b0e15] border border-white/15 p-6 max-w-md w-full space-y-4 shadow-2xl">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h3 className="text-sm font-chakra font-black uppercase text-white flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-amber-400" />
                <span>Emite Avertisment Utilizator</span>
              </h3>
              <button onClick={() => setWarnModal({ open: false, userId: "", username: "", points: 1, reason: "" })} className="text-zinc-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3 text-xs font-mono">
              <div>
                <label className="text-zinc-400 block mb-1">ID Utilizator</label>
                <input type="text" value={warnModal.userId} disabled className="w-full bg-black/60 border border-white/10 px-3 py-2 text-zinc-400" />
              </div>
              <div>
                <label className="text-zinc-400 block mb-1">Puncte Avertisment (3p = 24h suspendare, 5p = 7 zile ban)</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={warnModal.points}
                  onChange={(e) => setWarnModal({ ...warnModal, points: e.target.value })}
                  className="w-full bg-black/60 border border-white/15 px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="text-zinc-400 block mb-1">Motiv Avertisment</label>
                <textarea
                  rows="3"
                  value={warnModal.reason}
                  onChange={(e) => setWarnModal({ ...warnModal, reason: e.target.value })}
                  placeholder="Ex: Limbaj neadecvat, spam, provocare la cearta..."
                  className="w-full bg-black/60 border border-white/15 px-3 py-2 text-white"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
              <button onClick={() => setWarnModal({ open: false, userId: "", username: "", points: 1, reason: "" })} className="px-4 py-2 bg-white/5 text-zinc-400 hover:text-white text-xs font-chakra uppercase font-bold">
                Anuleaza
              </button>
              <button onClick={handleIssueWarn} className="px-4 py-2 bg-amber-500 text-black text-xs font-chakra font-black uppercase shadow-lg">
                Confirma Avertisment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
